import { createContext, PropsWithChildren, useEffect, useState } from 'react'
import { useAuth0, User } from '@auth0/auth0-react'

import * as Diagnostics from '../../tools/dom/diagnostics'
import { useProvideContext } from '../../hooks/context'

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export interface AuthContextValue {
    isLoggedIn: boolean
    isLoading: boolean
    accessToken?: string | null
    auth0User?: User
    logout: () => Promise<void>
}

export default function ({ children }: PropsWithChildren) {
    const {
        isAuthenticated,
        error,
        isLoading: auth0Loading,
        user: auth0User,
        loginWithRedirect,
        logout,
        getAccessTokenSilently,
    } = useAuth0()

    const [isLoading, setIsLoading] = useState(true)
    const [accessToken, setAccessToken] = useState<string | null>(null)

    useEffect(() => {
        // Inherit auth0 loading state.
        setIsLoading(isLoading || auth0Loading)
    }, [auth0Loading])

    useEffect(() => {
        if (!auth0Loading && !isAuthenticated)
            loginWithRedirect({
                appState: {
                    href: window.location.href,
                },
            })
    }, [auth0Loading, isAuthenticated])

    useEffect(() => {
        const fetchToken = async () => {
            if (!isAuthenticated) return
            try {
                const token = await getAccessTokenSilently()
                setAccessToken(token)
                setIsLoading(false)
            } catch (error) {
                Diagnostics.append('Auth', error)
            }
        }
        fetchToken()
    }, [isAuthenticated, getAccessTokenSilently])

    useEffect(() => {
        if (error) Diagnostics.append('Auth', error)
    }, [error])

    const logoutWithParams = () =>
        logout({
            logoutParams: {
                returnTo: window.location.origin,
            },
        })

    const isLoggedIn = !!auth0User && !!accessToken && isAuthenticated

    const value = {
        isLoggedIn,
        isLoading,
        auth0User,
        accessToken,
        logout: logoutWithParams,
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuthContext = () => useProvideContext(AuthContext, 'Auth')
